define([
	"csi",
	"csi/action",
	"csi/access/crews",
	"csi/api",
	"csi/canvas/storeFactory",
	"csi/comm",
	"csi/config",
	"csi/config/preferences",
	"csi/locale",
	"csi/page/ComparePageStore",
	"csi/registry/bannerRegistry",
	"csi/registry/pageStoreRegistry",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/resolver",
	"csi/state/rootStore",
	"csi/view",
	"csi/xml",
	"./ApparelBOM/ApparelBOMBanners",
	"./ApparelBOM/ApparelBOMPageStore",
	"Specification/BOM/BOMCompareBanner",
	"./SizeChart", // include so that Pre/On/PostAction functions will resolve correctly
], function (
	csi,
	csiAction,
	crews,
	api,
	storeFactory,
	comm,
	config,
	configPreferences,
	locale,
	ComparePageStore,
	{ registerBanner },
	{ registerPageStore },
	reflection,
	reflectionHints,
	resolver,
	{ routerStore },
	csiView,
	xml,
	ApparelBOMBanners,
	ApparelBOMPageStore,
	BOMCompareBanner
) {
	const APPAREL_BOM = "ApparelBOM";
	var APPAREL_BOM_REVISION = "ApparelBOMRevision";
	var ARTWORK_REVISION = "ArtworkRevision";
	var COLORWAY = "Colorway";
	var IMAGE_DATASHEET_REVISION = "ImageDataSheetRevision";
	var PART_MATERIAL = "PartMaterial";
	var PRINT_DESIGN_REVISION = "PrintDesignRevision";
	var PRINT_DESIGN_COLORS = "PrintDesignColor";
	var PROPERTIES_DATASHEET_REVISION = "PropertiesDataSheetRevision";
	var REVIEW_COLLECTION_REVISION = "ReviewCollectionRevision";
	var REVIEW_STYLE_REVISION = "ReviewStyleRevision";
	var SAMPLE = "Sample";
	var SIZE_CHART_DIMENSION = "SizeChartDimension";
	var SIZE_CHART_REVISION = "SizeChartRevision";
	var SIZE_CHART_REVIEW_REVISION = "SizeChartReviewRevision";
	var STYLE = "Style";

	storeFactory.add(APPAREL_BOM_REVISION, COLORWAY, "Child:BOMProductColors");
	storeFactory.add(APPAREL_BOM_REVISION, PART_MATERIAL, "Child:Items");

	storeFactory.add(ARTWORK_REVISION, COLORWAY, null, function (node, cache) {
		if (node.Colorways.length > 0) {
			// Primary, if no results, then secondary path.
			return "Child:Colorways";
		} else {
			// Secondary, if no results from primary
			return "(ArtworkRevision)Child:__Parent__(Artwork)/Child:__Parent__(Style)/Child:ProductColors(Colorway)";
		}
	});

	storeFactory.add(
		REVIEW_COLLECTION_REVISION,
		COLORWAY,
		"Child:__Parent__/Child:__Parent__/Child:Hierarchy/Child:ProductColors"
	);
	storeFactory.add(REVIEW_COLLECTION_REVISION, STYLE, "Child:__Parent__/Child:__Parent__/Child:Hierarchy");
	storeFactory.add(IMAGE_DATASHEET_REVISION, COLORWAY, "Child:__Parent__/Child:__Parent__/Child:ProductColors");
	storeFactory.add(PRINT_DESIGN_REVISION, PRINT_DESIGN_COLORS, "Child:RevisionColors");
	storeFactory.add(PROPERTIES_DATASHEET_REVISION, COLORWAY, "Child:__Parent__/Child:__Parent__/Child:ProductColors");
	storeFactory.add(SIZE_CHART_REVISION, SIZE_CHART_DIMENSION, "Child:Items");
	storeFactory.add(
		SIZE_CHART_REVIEW_REVISION,
		SIZE_CHART_DIMENSION,
		"Child:__Parent__/Child:ReviewSizeChart/Child:Items"
	);
	storeFactory.add(SIZE_CHART_REVIEW_REVISION, SAMPLE, "Child:__Parent__/Child:ReviewSizes/Child:ReviewSizeSamples");
	storeFactory.add(REVIEW_STYLE_REVISION, COLORWAY, "Child:__Parent__/Child:__Parent__/Child:ProductColors");
	storeFactory.add(REVIEW_STYLE_REVISION, SAMPLE, "Child:__Parent__/Child:DataSheetSamples");

	// register BOM and SizeChart-specific controllers for state actions
	csiAction.registerControllers("ApparelBOM", {
		"MakePending": "Specification/BOM/PhaseFreezeController",
		"EndPhase": "Specification/BOM/PhaseFreezeController",
		"StartApprovalWorkflow": "Specification/BOM/PhaseFreezeStartApprovalWorkflowController",
		"ReopenPhase": "Specification/BOM/ReopenPhaseController",
	});

	csiAction.registerControllers("SizeChart", {
		"ReviewReopen": "ProductSpec/SizeChart/ReviewReopen",
		"ReviewStart": "ProductSpec/SizeChart/ReviewStart",
	});

	api.addSpecialPath("ApparelDimension", "Child:Actual");

	locale.registerDisplayNameResolver("CollectionMatrix", locale.displayNameOrType);
	locale.registerDisplayNameResolver("MaterialSchedule", locale.displayNameOrType);

	locale.registerDisplayNameResolver("IncrementSize", function (node, cache, loc) {
		// get localized name of referenced ProductSize, otherwise will only render node name (no localization)
		var size = cache && cache.get(node.IncrementSize);
		return locale.getNodeStoredName(size || node, cache, loc);
	});

	locale.registerDisplayNameResolver("SizeChartReviewSize", function (node, cache, loc) {
		// get localized name of referenced ProductSize, otherwise will only render node name (no localization)
		var size = cache && cache.get(node.ReviewSize);
		return locale.getNodeStoredName(size || node, cache, loc);
	});

	reflectionHints.setLocationHistoryKeyCallback("SizeChart", (node, revisionNode) => {
		// track selected tab differently when in review state
		return `SizeChart${revisionNode.State == reflection.REVISION_REVIEW ? "-Review" : ""}`;
	});

	/* --- BOM Compare --- */
	/** initialize the banner for BOM Compare  */
	registerBanner(APPAREL_BOM, ApparelBOMBanners.default);

	/** initialize the page store for BOM Compare  */
	const _initCompareStore = async (node, revisionNode, cache) => {
		return new ApparelBOMPageStore.default(node, revisionNode, cache);
	};

	// register the page store for PP node
	registerPageStore(APPAREL_BOM, _initCompareStore);

	return {
		/**
		 * Handles the DimensionList table row actions
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		onAccessActionDimensionListActions: function (ctx, action) {
			var dimensionNode = ctx.getNode();
			var scRevNode = ctx.getParent();
			var scNode = scRevNode != null ? ctx.cache.getNode(scRevNode.__Parent__) : null;
			var lockShapeDimensionList = config.getNodeSetting(scNode, "LockShapeDimensionList");
			return (dimensionNode != null && dimensionNode.ShapeSysID == "") || !lockShapeDimensionList;
		},

		onAccessActionSyncColorToTheme: function (/*Context*/ ctx, /*Action*/ action) {
			const bomRev = ctx.cache.getNode(routerStore.locationNode.CurrentRevision, true);
			return xml.nodeHasRight(bomRev, xml.RIGHT_WRITE) && !xml.nodeHasFlag(bomRev, xml.NODE_FLAG_FROZEN);
		},

		onAccessActionSaveSizeChartTemplate: function (/*Context*/ ctx, /*Action*/ action) {
			// Size Chart "Save As Template" should requires Edit right on the global SizeChartAdmin node
			var sizeChartAdmin = resolver.resolveNode("centric://APPAREL/SizeChartAdmin", true);
			return sizeChartAdmin && xml.nodeHasRight(sizeChartAdmin, xml.RIGHT_WRITE);
		},

		onActionNewFromArtwork: function (ac) {
			// summary: when copying artwork from another style, make sure to reset colorways
			var artwork = ac.m_aSources[0];

			if (ac.m_urlParent != artwork.__Parent__) {
				ac.m_writer
					.changeNode(xml.getUrlPath(ac.m_urlNode, reflection.PATH_CURRENT_REVISION))
					.changeAttribute("Colorways", xml.ATTR_REFLIST, []);
			}
		},

		onActionDataSheetSizeLabelSizeLabel: function (/*Controller*/ ac) {
			//summary: populate DataSheetSizeLabelRevision initial values of 'Sizes' and 'Comments' from selected Site library Size Label
			var sizeLabel = ac.getNode();
			var revision = ac.getParent();
			var w = ac.m_writer;
			var urlComment;

			w.deleteQuery(
				w.createQuery()
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, "Comment")
					.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, revision.$URL)
			);

			if (sizeLabel && sizeLabel.Comment) {
				urlComment = comm.createUrl("Comment");

				w.createNode(urlComment, "Comment").changeAttribute("Comment", xml.ATTR_STRING, sizeLabel.Comment);
			}

			var cn = w.changeNode(revision.$URL);
			cn.changeAttribute("Sizes", xml.ATTR_REFLIST, sizeLabel ? sizeLabel.Sizes : []);
			if (urlComment) cn.changeAttribute("RevisionComments", xml.ATTR_REFVECTOR, [urlComment]);

			ac.bRefreshNode = true;
		},

		/**
		 * @override
		 * Expression Rule for editing attributes on SizeChartRevision node (Product Group, Increment, Size Range)
		 */
		onAccessInlineActionSizeChart: function (ctx, action) {
			var attr = action.aggregateId;
			var node = ctx.getNode();

			if (xml.nodeAttributeHasFlag(node, attr, xml.ATTR_FLAG_NOEDIT)) {
				return false;
			}

			var parent = ctx.getParent();
			var cache = ctx.cache;

			// For Increment we need extra step of Parent
			if (parent.$Type != "SizeChart") {
				parent = cache.getNode(parent.__Parent__);
			}

			return node[attr] == csi.EMPTY_URL || parent.__Parent__ == csi.EMPTY_URL;
		},

		/**
		 * @override
		 * C8-26803: some Size Chart fields should not be editable in Evaluate State
		 */
		onAccessActionSizeChart: function (ctx, action) {
			var attr = action.heading.m_id;
			var node = ctx.getNode();

			return !xml.nodeAttributeHasFlag(node, attr, xml.ATTR_FLAG_NOEDIT);
		},

		onAccessActionCopySourceDataSheet(ctx, action) {
			const roles = ["TDS - BOM Administrator", "Product Specification Administrator", 'TDS - BOM Creator'];
			return crews.isInRoles(roles, ctx.getNode()) || crews.isInRoles(roles, ctx.view.getViewNode());
		},

		/**
		 * Refresh SizeChartReviews after adding a referenced Shape SizeChart or promoting Style Size Chart
		 * @param {Action} action
		 */
		postActionReferenceSizeChart(ac) {
			const view = csiView.getView("SizeChartReviews");
			if (view) {
				view.refreshWidget();
			}
		},
	};
});
